export interface DiffPart {
  value: string;
  added?: boolean;
  removed?: boolean;
}

/**
 * Tokenize text into alternating word/whitespace tokens.
 * Preserves newlines and all whitespace so paragraph breaks survive diffing.
 */
function tokenize(text: string): string[] {
  return text.match(/\S+|\s+/g) || [];
}

/**
 * A word-level diff algorithm based on Longest Common Subsequence (LCS).
 * Preserves whitespace (newlines, paragraph breaks) in the output.
 */
export function diffWords(oldText: string, newText: string): DiffPart[] {
  if (!oldText) return [{ value: newText, added: true }];
  if (!newText) return [{ value: oldText, removed: true }];

  const oldTokens = tokenize(oldText);
  const newTokens = tokenize(newText);

  const m = oldTokens.length;
  const n = newTokens.length;

  // Compute LCS Matrix
  const dp: number[][] = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldTokens[i - 1] === newTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find diff
  let i = m;
  let j = n;
  const raw: DiffPart[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      raw.unshift({ value: oldTokens[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.unshift({ value: newTokens[j - 1], added: true });
      j--;
    } else if (i > 0 && (j === 0 || dp[i][j - 1] < dp[i - 1][j])) {
      raw.unshift({ value: oldTokens[i - 1], removed: true });
      i--;
    }
  }

  // Merge consecutive parts with the same type to reduce span count
  const merged: DiffPart[] = [];
  for (const part of raw) {
    const last = merged[merged.length - 1];
    if (last && last.added === part.added && last.removed === part.removed) {
      last.value += part.value;
    } else {
      merged.push({ ...part });
    }
  }

  return merged;
}

/**
 * Calculates simple statistics about the changes
 */
export function getDiffStats(diffs: DiffPart[]) {
  const added = diffs.filter(d => d.added).length;
  const removed = diffs.filter(d => d.removed).length;
  return { added, removed };
}
